import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Avatar,
  Tooltip,
  Divider,
  Alert,
  Pagination,
  Stack,
  InputAdornment,
  Collapse,
  Snackbar,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  AccessTime as AccessTimeIcon,
  Flag as FlagIcon,
  ArrowBack as ArrowBackIcon,
  FilterList as FilterListIcon,
  Message as MessageIcon,
  CalendarToday as CalendarTodayIcon,
  Sort as SortIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import config from '../../config';
import { authFetch, handleApiResponse, describeApiError } from '../../api/authFetch';
import MarkdownContent from '../chat/MarkdownContent';
import { adminColors } from '../../theme/adminTheme';
import { ROLES, isMasterAdmin, isAdminRole, isTeacher } from '../../constants/roles';
import RoleChip from '../RoleChip';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const CATEGORY_LABELS = {
  threats_violence: 'Threats & Violence',
  weapons: 'Weapons',
  bullying_harassment: 'Bullying & Harassment',
  profanity: 'Profanity',
  controlled_substances: 'Controlled Substances',
  sexual_misconduct: 'Sexual Misconduct',
  self_harm: 'Self-Harm',
  hate_speech: 'Hate Speech',
};

function formatTimestamp(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(iso) {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ─── CSV export utilities ─────────────────────────────────────────────────────

/**
 * Escape a single CSV field value:
 * - wrap in double-quotes
 * - escape any existing double-quotes by doubling them
 * - strip/replace literal newlines inside the field so Excel doesn't break rows
 */
function csvField(value) {
  if (value === null || value === undefined) return '';
  const str = String(value).replace(/\r?\n/g, ' ↵ ');
  return '"' + str.replace(/"/g, '""') + '"';
}

function buildConversationCSV(username, school, messages) {
  const headers = ['timestamp', 'role', 'speaker', 'content', 'flagged', 'flag_categories', 'deleted', 'document'];
  const rows = [headers.map(csvField).join(',')];
  for (const msg of messages) {
    const isFlagged = msg.safety_flags && msg.safety_flags.length > 0;
    const categories = isFlagged
      ? msg.safety_flags.flatMap((f) => f.categories || []).map((c) => CATEGORY_LABELS[c] || c).join('; ')
      : '';
    rows.push([
      csvField(msg.timestamp || ''),
      csvField(msg.role || ''),
      csvField(msg.role === 'user' ? username : 'Assistant'),
      csvField(msg.content || ''),
      csvField(isFlagged ? 'yes' : 'no'),
      csvField(categories),
      csvField(msg.is_deleted ? 'yes' : 'no'),
      csvField(msg.document_filename || ''),
    ].join(','));
  }
  return rows.join('\n');
}

function buildUserListCSV(users) {
  const headers = ['username', 'school', 'role', 'message_count', 'pending_flags', 'last_active', 'access_granted'];
  const rows = [headers.map(csvField).join(',')];
  for (const u of users) {
    rows.push([
      csvField(u.username),
      csvField(u.school || ''),
      csvField(u.role || ''),
      csvField(u.message_count ?? 0),
      csvField(u.pending_flag_count ?? 0),
      csvField(u.last_active ? formatTimestamp(u.last_active) : 'Never'),
      csvField(u.access_granted !== false ? 'yes' : 'no'),
    ].join(','));
  }
  return rows.join('\n');
}

function triggerDownload(csvText, filename) {
  const blob = new Blob(['\uFEFF' + csvText], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─── Sort helpers ─────────────────────────────────────────────────────────────

const COLUMNS = [
  { id: 'username', label: 'User', numeric: false, sortable: true },
  { id: 'school', label: 'School', numeric: false, sortable: true },
  { id: 'role', label: 'Role', numeric: false, sortable: true },
  { id: 'message_count', label: 'Messages', numeric: true, sortable: true },
  { id: 'last_active', label: 'Last Active', numeric: false, sortable: true },
  { id: 'pending_flag_count', label: 'Flags', numeric: true, sortable: true },
  { id: 'actions', label: '', numeric: false, sortable: false },
];

function descendingComparator(a, b, orderBy) {
  const av = a[orderBy] ?? '';
  const bv = b[orderBy] ?? '';
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// ─── User List Panel ──────────────────────────────────────────────────────────

function UserListPanel({ onSelectUser, selectedUsername, currentUser }) {
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterInput, setFilterInput] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [schoolInput, setSchoolInput] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Sort state
  const [orderBy, setOrderBy] = useState('username');
  const [order, setOrder] = useState('asc');

  // Pagination (client-side on the fetched batch)
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  // Backend hard cap is le=100; fetch up to 100 per request
  const FETCH_SIZE = 100;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    const params = new URLSearchParams({ page: 1, page_size: FETCH_SIZE });
    if (filterInput) params.set('search', filterInput);
    if (roleFilter) params.set('role', roleFilter);
    if (schoolFilter) params.set('school', schoolFilter);

    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/admin/users?${params.toString()}`);
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      setAllUsers(data.users || []);
      setTotal(data.total || 0);
      setPage(1);
    } catch (e) {
      setError(`Couldn't load users. ${describeApiError(e)}`);
    } finally {
      setLoading(false);
    }
  }, [filterInput, roleFilter, schoolFilter, navigate]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Client-side sort
  const sortedUsers = useMemo(
    () => [...allUsers].sort(getComparator(order, orderBy)),
    [allUsers, order, orderBy]
  );

  // Client-side pagination over sorted list
  const pageUsers = useMemo(
    () => sortedUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sortedUsers, page, PAGE_SIZE]
  );
  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / PAGE_SIZE));

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(col);
      setOrder(col === 'username' || col === 'school' || col === 'role' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const handleApplyFilters = () => {
    setFilterInput(searchTerm);
    setSchoolFilter(schoolInput);
    setPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleApplyFilters();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterInput('');
    setSchoolInput('');
    setSchoolFilter('');
    setRoleFilter('');
    setPage(1);
  };

  const activeFilterCount = [filterInput, schoolFilter, roleFilter].filter(Boolean).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ── Toolbar ── */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: '1px solid #e0e0e0', backgroundColor: 'white', flexShrink: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#18181b" lineHeight={1.2}>
              Users
            </Typography>
            {!loading && (
              <Typography variant="caption" color="text.secondary">
                {sortedUsers.length}{total > sortedUsers.length ? ` of ${total}` : ''} user{sortedUsers.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
          <Box display="flex" gap={0.5}>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={fetchUsers} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export user list as CSV">
              <IconButton
                size="small"
                onClick={() => {
                  const csv = buildUserListCSV(sortedUsers);
                  const dateTag = new Date().toISOString().slice(0, 10);
                  triggerDownload(csv, `user_list_${dateTag}.csv`);
                }}
                disabled={loading || sortedUsers.length === 0}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={showFilters ? 'Hide filters' : 'Show filters'}>
              <IconButton
                size="small"
                onClick={() => setShowFilters((v) => !v)}
                color={activeFilterCount > 0 ? 'primary' : 'default'}
              >
                <FilterListIcon fontSize="small" />
                {activeFilterCount > 0 && (
                  <Box
                    component="span"
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#4f46e5',
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Search bar always visible */}
        <Box display="flex" gap={1}>
          <TextField
            placeholder="Search username or school…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#9e9e9e', fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearFilters}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Button variant="contained" size="small" onClick={handleApplyFilters} sx={{ minWidth: 70 }}>
            Search
          </Button>
        </Box>

        {/* Collapsible filter panel */}
        <Collapse in={showFilters}>
          <Box display="flex" gap={1.5} mt={1.5} flexWrap="wrap">
            {isMasterAdmin(currentUser?.role) && (
              <TextField
                label="School"
                placeholder="e.g. WESTVIEW HIGH"
                value={schoolInput}
                onChange={(e) => setSchoolInput(e.target.value.toUpperCase())}
                onKeyPress={handleKeyPress}
                size="small"
                sx={{ minWidth: 200, flex: 1 }}
              />
            )}
            <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={roleFilter}
                label="Role"
                onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
              >
                <MenuItem value="">All roles</MenuItem>
                <MenuItem value={ROLES.STUDENT}>Student</MenuItem>
                {isAdminRole(currentUser?.role) && (
                  <MenuItem value={ROLES.TEACHER}>Teacher</MenuItem>
                )}
                <MenuItem value={ROLES.SCHOOL_ADMIN}>School Admin</MenuItem>
                {isMasterAdmin(currentUser?.role) && (
                  <MenuItem value={ROLES.MASTER_ADMIN}>Master Admin</MenuItem>
                )}
              </Select>
            </FormControl>
            {activeFilterCount > 0 && (
              <Button variant="outlined" size="small" onClick={handleClearFilters} sx={{ alignSelf: 'center' }}>
                Clear all
              </Button>
            )}
          </Box>
        </Collapse>
      </Box>

      {/* ── Table ── */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Box p={2}>
            <Alert severity="error" action={<Button size="small" onClick={fetchUsers}>Retry</Button>}>
              {error}
            </Alert>
          </Box>
        )}
        {!loading && !error && (
          <TableContainer sx={{ minWidth: 0 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.numeric ? 'center' : 'left'}
                      sx={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: '#5f6368',
                        backgroundColor: '#f4f4f6',
                        whiteSpace: 'nowrap',
                        py: 1,
                        px: col.id === 'actions' ? 1 : 1.5,
                      }}
                      sortDirection={col.sortable && orderBy === col.id ? order : false}
                    >
                      {col.sortable ? (
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : 'asc'}
                          onClick={() => handleSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pageUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      {activeFilterCount > 0
                        ? 'No users match these filters.'
                        : isTeacher(currentUser?.role)
                          ? "No students yet — once you're assigned to a class, its students will show up here."
                          : 'No users found.'}
                    </TableCell>
                  </TableRow>
                )}
                {pageUsers.map((user) => {
                  const isSelected = user.username === selectedUsername;
                  return (
                    <TableRow
                      key={user.id}
                      selected={isSelected}
                      hover
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#eef2ff !important' : undefined,
                        borderLeft: isSelected ? '3px solid #4f46e5' : '3px solid transparent',
                        '& td': { borderBottom: '1px solid #f0f0f0' },
                      }}
                      onClick={() => onSelectUser(user)}
                    >
                      {/* Username */}
                      <TableCell sx={{ py: 1, px: 1.5 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            sx={{
                              width: 30,
                              height: 30,
                              fontSize: 13,
                              bgcolor: isSelected ? '#4f46e5' : '#78909c',
                              flexShrink: 0,
                            }}
                          >
                            {(user.username?.[0] || 'U').toUpperCase()}
                          </Avatar>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            noWrap
                            sx={{ color: isSelected ? '#4338ca' : '#212121', maxWidth: 140 }}
                          >
                            {user.username}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* School */}
                      <TableCell sx={{ py: 1, px: 1.5, maxWidth: 120 }}>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {user.school || <span style={{ color: '#bdbdbd' }}>—</span>}
                        </Typography>
                      </TableCell>

                      {/* Role */}
                      <TableCell sx={{ py: 1, px: 1.5 }}>
                        <RoleChip role={user.role} sx={{ fontSize: 11, height: 22 }} />
                      </TableCell>

                      {/* Message count */}
                      <TableCell align="center" sx={{ py: 1, px: 1.5 }}>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                          <MessageIcon sx={{ fontSize: 14, color: '#4f46e5' }} />
                          <Typography variant="body2" fontWeight={600} color="#4f46e5">
                            {(user.message_count ?? 0).toLocaleString()}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Last active */}
                      <TableCell sx={{ py: 1, px: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {user.last_active
                            ? formatTimestamp(user.last_active)
                            : <span style={{ color: '#bdbdbd' }}>Never</span>}
                        </Typography>
                      </TableCell>

                      {/* Pending flags */}
                      <TableCell align="center" sx={{ py: 1, px: 1.5 }}>
                        {(user.pending_flag_count ?? 0) > 0 ? (
                          <Chip
                            icon={<FlagIcon sx={{ fontSize: '12px !important' }} />}
                            label={user.pending_flag_count}
                            size="small"
                            color="error"
                            sx={{ height: 22, fontSize: 11 }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      {/* View button */}
                      <TableCell sx={{ py: 1, px: 1 }}>
                        <Tooltip title="View conversation">
                          <IconButton
                            size="small"
                            color={isSelected ? 'primary' : 'default'}
                            onClick={(e) => { e.stopPropagation(); onSelectUser(user); }}
                          >
                            <ChatIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <Box sx={{ px: 2, py: 1, borderTop: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'white', flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sortedUsers.length)} of {sortedUsers.length}
          </Typography>
          <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} size="small" color="primary" />
        </Box>
      )}
    </Box>
  );
}

// ─── Conversation Panel ───────────────────────────────────────────────────────

function ConversationPanel({ user, onClose }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState('');
  const [appliedDateTo, setAppliedDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState('');
  const bottomRef = useRef(null);
  const PAGE_SIZE = 100;

  const fetchMessages = useCallback(async () => {
    if (!user?.username) return;
    setLoading(true);
    setError('');
    const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
    if (appliedDateFrom) params.set('date_from', appliedDateFrom);
    if (appliedDateTo) params.set('date_to', appliedDateTo);

    try {
      const res = await authFetch(
        `${config.API_BASE_URL}/api/admin/users/${encodeURIComponent(user.username)}/conversations?${params.toString()}`
      );
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      setMessages(data.messages || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(`Couldn't load this conversation. ${describeApiError(e)}`);
    } finally {
      setLoading(false);
    }
  }, [user?.username, page, appliedDateFrom, appliedDateTo, navigate]);

  useEffect(() => {
    setPage(1);
    setMessages([]);
    setAppliedDateFrom('');
    setAppliedDateTo('');
    setDateFrom('');
    setDateTo('');
  }, [user?.username]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleApplyFilters = () => {
    setPage(1);
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
  };

  const handleClearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setAppliedDateFrom('');
    setAppliedDateTo('');
    setPage(1);
  };

  // Export the FULL chat history (pages through all results)
  const handleExportCSV = async () => {
    if (!user?.username || exportLoading) return;
    setExportLoading(true);
    setExportError('');
    try {
      const EXPORT_PAGE_SIZE = 100;
      let allMessages = [];
      let exportPage = 1;
      let fetched = 0;
      let totalCount = Infinity;

      while (fetched < totalCount) {
        const params = new URLSearchParams({ page: exportPage, page_size: EXPORT_PAGE_SIZE });
        if (appliedDateFrom) params.set('date_from', appliedDateFrom);
        if (appliedDateTo) params.set('date_to', appliedDateTo);

        const res = await authFetch(
          `${config.API_BASE_URL}/api/admin/users/${encodeURIComponent(user.username)}/conversations?${params.toString()}`
        );
        const data = await handleApiResponse(res, navigate);
        if (data === null) return; // 401 — already redirected
        const batch = data.messages || [];
        allMessages = allMessages.concat(batch);
        totalCount = data.total || 0;
        fetched += batch.length;
        if (batch.length < EXPORT_PAGE_SIZE) break;
        exportPage++;
      }

      const csv = buildConversationCSV(user.username, user.school || '', allMessages);
      const dateTag = new Date().toISOString().slice(0, 10);
      triggerDownload(csv, `chat_history_${user.username}_${dateTag}.csv`);
    } catch (e) {
      console.error('CSV export failed:', e);
      setExportError(`The export couldn't be created. ${describeApiError(e)}`);
    } finally {
      setExportLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (!user) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        gap={2}
        sx={{ color: 'text.secondary', px: 4 }}
      >
        <ChatIcon sx={{ fontSize: 56, color: '#bdbdbd' }} />
        <Typography variant="h6" color="text.secondary" textAlign="center">
          Select a user to view their conversation history
        </Typography>
        <Typography variant="body2" color="text.disabled" textAlign="center">
          Click any user in the list on the left to load their full chat history.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Conversation header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          backgroundColor: '#18181b',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexShrink: 0,
        }}
      >
        {onClose && (
          <IconButton onClick={onClose} sx={{ color: 'white', p: 0.5 }} size="small">
            <ArrowBackIcon />
          </IconButton>
        )}
        <Avatar sx={{ width: 36, height: 36, bgcolor: '#4f46e5', fontSize: 15 }}>
          {(user.username?.[0] || 'U').toUpperCase()}
        </Avatar>
        <Box flex={1}>
          <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
            {user.username}
          </Typography>
          <Box display="flex" alignItems="center" gap={2} mt={0.25}>
            {user.school && (
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                <SchoolIcon sx={{ fontSize: 12, mr: 0.3, verticalAlign: 'middle' }} />
                {user.school}
              </Typography>
            )}
            <Typography variant="caption" sx={{ opacity: 0.65 }}>
              <MessageIcon sx={{ fontSize: 12, mr: 0.3, verticalAlign: 'middle' }} />
              {user.message_count ?? total} total messages
            </Typography>
            {(user.pending_flag_count ?? 0) > 0 && (
              <Chip
                icon={<FlagIcon sx={{ fontSize: '12px !important' }} />}
                label={`${user.pending_flag_count} pending flag${user.pending_flag_count !== 1 ? 's' : ''}`}
                size="small"
                color="error"
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
          </Box>
        </Box>

        <Tooltip title={appliedDateFrom || appliedDateTo ? 'Toggle date filters (active)' : 'Toggle date filters'}>
          <IconButton
            onClick={() => setShowFilters((v) => !v)}
            sx={{ color: 'white' }}
            size="small"
          >
            <FilterListIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Export full chat history as CSV">
          <span>
            <IconButton
              onClick={handleExportCSV}
              disabled={exportLoading}
              sx={{ color: 'white' }}
              size="small"
            >
              {exportLoading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <DownloadIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Date filter bar */}
      {showFilters && (
        <Box
          sx={{
            px: 3,
            py: 1.5,
            backgroundColor: '#f7f7f8',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            flexShrink: 0,
          }}
        >
          <TextField
            label="From date"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="To date"
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <Button variant="contained" size="small" onClick={handleApplyFilters}>
            Apply
          </Button>
          {(appliedDateFrom || appliedDateTo) && (
            <Button variant="outlined" size="small" onClick={handleClearFilters}>
              Clear
            </Button>
          )}
          {(appliedDateFrom || appliedDateTo) && (
            <Typography variant="caption" color="text.secondary">
              Showing {total} message{total !== 1 ? 's' : ''} in range
            </Typography>
          )}
        </Box>
      )}

      {/* Messages area */}
      <Box sx={{ flex: 1, overflowY: 'auto', backgroundColor: '#f4f4f6' }}>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Box p={3}>
            <Alert
              severity="error"
              action={<Button size="small" onClick={fetchMessages}>Retry</Button>}
            >
              {error}
            </Alert>
          </Box>
        )}
        {!loading && !error && messages.length === 0 && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={4} gap={1}>
            <ChatIcon sx={{ fontSize: 40, color: '#bdbdbd' }} />
            <Typography color="text.secondary" variant="body2" textAlign="center">
              No messages found{appliedDateFrom || appliedDateTo ? ' in the selected date range' : ''}.
            </Typography>
          </Box>
        )}
        {!loading && messages.map((msg, idx) => {
          const isFlagged = msg.safety_flags && msg.safety_flags.length > 0;
          const isUser = msg.role === 'user';
          const isDeleted = !!msg.is_deleted;
          const prevMsg = messages[idx - 1];
          const showDateDivider =
            idx === 0 ||
            (prevMsg &&
              new Date(msg.timestamp).toDateString() !== new Date(prevMsg.timestamp).toDateString());

          return (
            <React.Fragment key={msg.id}>
              {showDateDivider && msg.timestamp && (
                <Box
                  display="flex"
                  alignItems="center"
                  my={1.5}
                  px={3}
                >
                  <Divider sx={{ flex: 1 }} />
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ px: 1.5, whiteSpace: 'nowrap' }}
                  >
                    <CalendarTodayIcon sx={{ fontSize: 11, mr: 0.5, verticalAlign: 'middle' }} />
                    {new Date(msg.timestamp).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>
              )}
              <Box
                sx={{
                  px: 3,
                  py: 1.25,
                  backgroundColor: isFlagged
                    ? '#fff8e1'
                    : isDeleted
                    ? '#f7f7f7'
                    : isUser
                    ? 'white'
                    : '#f3f6fb',
                  borderLeft: isFlagged
                    ? '4px solid #ff6d00'
                    : isDeleted
                    ? '4px dashed #bdbdbd'
                    : '4px solid transparent',
                  borderBottom: '1px solid #eeeeee',
                  opacity: isDeleted ? 0.78 : 1,
                }}
              >
                {/* Message header */}
                <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: 12,
                      bgcolor: isUser ? adminColors.accent : adminColors.accentDark,
                    }}
                  >
                    {isUser ? (user.username?.[0] || 'U').toUpperCase() : 'AI'}
                  </Avatar>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    {isUser ? user.username : 'Assistant'}
                  </Typography>
                  {isDeleted && (
                    <Tooltip title="Part of a chat the student cleared — hidden from the student but retained for admin review">
                      <Chip
                        label="Deleted"
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: 10, color: '#9e9e9e', borderColor: '#bdbdbd' }}
                      />
                    </Tooltip>
                  )}
                  <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>
                    <AccessTimeIcon sx={{ fontSize: 11, mr: 0.3, verticalAlign: 'middle' }} />
                    {formatTimestamp(msg.timestamp)}
                  </Typography>
                </Box>

                {/* Message content - rendered through the same markdown + math
                    pipeline as the student-facing ChatMessage so assistant
                    replies don't show literal **markdown** or raw LaTeX. */}
                <MarkdownContent
                  content={msg.content}
                  sx={{
                    pl: 4.5,
                    fontSize: '0.875rem',
                    lineHeight: 1.65,
                    color: adminColors.ink,
                    wordBreak: 'break-word',
                  }}
                />

                {/* Safety flag chips */}
                {isFlagged && (
                  <Box display="flex" gap={0.5} flexWrap="wrap" pl={4.5} mt={0.75}>
                    <Chip
                      icon={<FlagIcon />}
                      label="Flagged"
                      size="small"
                      color="warning"
                      sx={{ fontSize: 11 }}
                    />
                    {msg.safety_flags.map((f) =>
                      (f.categories || []).map((cat) => (
                        <Chip
                          key={`${f.id}-${cat}`}
                          label={CATEGORY_LABELS[cat] || cat}
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ fontSize: 11 }}
                        />
                      ))
                    )}
                  </Box>
                )}

                {/* Document annotation */}
                {msg.document_filename && (
                  <Typography variant="caption" color="text.disabled" sx={{ pl: 4.5, display: 'block', mt: 0.5 }}>
                    📎 {msg.document_filename}
                  </Typography>
                )}
              </Box>
            </React.Fragment>
          );
        })}
        <div ref={bottomRef} />
      </Box>

      {/* Pagination footer */}
      {totalPages > 1 && (
        <Box
          sx={{
            p: 1.5,
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'white',
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} messages
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            size="small"
            color="primary"
          />
        </Box>
      )}

      <Snackbar
        open={!!exportError}
        autoHideDuration={6000}
        onClose={() => setExportError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setExportError('')} severity="error" variant="filled" sx={{ width: '100%' }}>
          {exportError}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ─── Main ConversationViewer ──────────────────────────────────────────────────

export default function ConversationViewer({ currentUser }) {
  const [selectedUser, setSelectedUser] = useState(null);

  // Left panel is wider (table) when a conversation is open, full-width when not
  const leftWidth = selectedUser ? { xs: 0, md: 480 } : { xs: '100%', md: '100%' };
  const leftMinWidth = selectedUser ? { xs: 0, md: 480 } : { xs: '100%', md: '100%' };

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 64px)',
        backgroundColor: '#f7f7f8',
        overflow: 'hidden',
      }}
    >
      {/* Left panel: user list table */}
      <Box
        sx={{
          width: leftWidth,
          minWidth: leftMinWidth,
          maxWidth: selectedUser ? 480 : undefined,
          borderRight: selectedUser ? '1px solid #e0e0e0' : 'none',
          backgroundColor: 'white',
          display: { xs: selectedUser ? 'none' : 'flex', md: 'flex' },
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'width 0.25s ease, min-width 0.25s ease',
          flexShrink: 0,
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
          },
        }}
      >
        <UserListPanel
          onSelectUser={setSelectedUser}
          selectedUsername={selectedUser?.username}
          currentUser={currentUser}
        />
      </Box>

      {/* Right panel: conversation — only rendered when a user is selected */}
      {selectedUser && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: 'white',
          }}
        >
          <ConversationPanel
            user={selectedUser}
            onClose={() => setSelectedUser(null)}
          />
        </Box>
      )}
    </Box>
  );
}
